import { useTranslation } from 'react-i18next'
import { FormikContextType, useFormikContext } from 'formik'
import { Stack, Columns, Column, Checkbox, RadioBarItem } from '@machobear/design-system'
import { getAreaMeasurementLabel } from '@/utils/listings'
import useProfile from '@/hooks/use-profile'
import { FieldInput, FieldRadioBar } from '@/components/ds-form'

import DescriptionField from './description-field'
import PropertyRulesFields from './property-rules-fields'
import { FormBox, FormSection, FormBoxSkeleton } from '../../components'
import { DetailsFormValues } from '../../types'

const UnitFields = ({
  isMultiUnit,
  setFieldValue,
  values
}: {
  isMultiUnit: boolean
  setFieldValue: any
  values: DetailsFormValues
}) => {
  const { t } = useTranslation()
  const isApartment = values.unit_type_txt_id === 'condo'
  const isHouse = values.unit_type_txt_id === 'house'

  return (
    <FormSection label={t('Unit info')}>
      <Columns space="gutter" collapseBelow="tablet" alignY="center">
        <Column width="1/2">
          <FieldInput
            name="unit_number"
            label={
              isMultiUnit
                ? t('l.listing.create.plan_name.label', 'Plan name')
                : isApartment
                  ? t('l.listing.create.unit_number.label', 'Unit number')
                  : t('l.listing.create.unit_name_number.label', 'Unit name / number')
            }
            required={isMultiUnit || isApartment}
          />
        </Column>
        {!isMultiUnit ? (
          <Column width="1/2">
            <Checkbox
              id="hide_unit_number"
              name="hide_unit_number"
              checked={values.hide_unit_number === 1}
              label={
                !isHouse
                  ? t('l.listing.create.hide_unit_number.label', 'Hide unit number')
                  : t('l.listing.create.hide_street_number.label', 'Hide unit and street number')
              }
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const isChecked = e?.target?.checked
                setFieldValue('hide_unit_number', isChecked ? 1 : 0)
              }}
            />
          </Column>
        ) : null}
      </Columns>
    </FormSection>
  )
}

const MultiUnitListingFields = ({ sizeLabel }: { sizeLabel: string }) => {
  const { t } = useTranslation()

  return (
    <Stack space="xlarge" align="left">
      <FormSection label={t('Multi-unit details', 'Multi-unit details')}>
        <Columns space="gutter" collapseBelow="tablet">
          <Column width="1/2">
            <FieldInput
              name="gr_min_size"
              label={`${t('Minimum size', 'Minimum size')} ${sizeLabel}`}
              type="number"
              required
            />
          </Column>
          <Column width="1/2">
            <FieldInput
              name="gr_max_size"
              label={`${t('Maximum size', 'Maximum size')} ${sizeLabel}`}
              type="number"
              required
            />
          </Column>
        </Columns>
        <Columns space="gutter" collapseBelow="tablet">
          <Column width="1/2">
            <FieldInput
              name="gr_count"
              label={t('l.listing.edit.units_available.label', 'Number of units available')}
            />
          </Column>
          <Column width="1/2">
            <FieldInput
              name="gr_floors"
              label={t('l.listing.edit.available_floors.label', 'Available floors')}
            />
          </Column>
        </Columns>
      </FormSection>
      <FormSection label={t('Overview', 'Overview')}>
        <Columns space="gutter" collapseBelow="tablet">
          <Column width="1/2">
            <FieldInput
              name="count_bedrooms"
              label={t('Bedrooms', 'Bedrooms')}
              type="number"
              required
            />
          </Column>
          <Column width="1/2">
            <FieldInput
              name="count_full_bathrooms"
              label={t('Bathrooms', 'Bathrooms')}
              type="number"
              required
            />
          </Column>
        </Columns>
        <Columns space="gutter" collapseBelow="tablet">
          <Column width="1/2">
            <FieldInput name="count_dens" label={t('Den/flex', 'Den/flex')} type="number" />
          </Column>
          <Column width="1/2">
            <FieldInput
              name="max_occupancy"
              label={t('Maximum occupancy', 'Maximum occupancy')}
              type="number"
            />
          </Column>
        </Columns>
      </FormSection>
    </Stack>
  )
}

const EntireFields = ({ sizeLabel, requireSize }: { sizeLabel: string; requireSize: boolean }) => {
  const { t } = useTranslation()

  return (
    <Stack space="gutter" align="left">
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="count_bedrooms"
            label={t('Bedrooms', 'Bedrooms')}
            type="number"
            inputMode="numeric"
            required
          />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="count_full_bathrooms"
            label={t('Bathrooms', 'Bathrooms')}
            type="number"
            inputMode="decimal"
            required
          />
        </Column>
      </Columns>
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput name="count_dens" label={t('Den/flex', 'Den/flex')} type="number" />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="size"
            label={`${t('Size', 'Size')} ${sizeLabel}`}
            type="number"
            inputMode="numeric"
            required={requireSize}
          />
        </Column>
      </Columns>
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="max_occupancy"
            label={t('Maximum occupancy', 'Maximum occupancy')}
            type="number"
            inputMode="numeric"
          />
        </Column>
      </Columns>
    </Stack>
  )
}

const PartialFields = ({
  sizeLabel,
  requireSize,
  privateEntrance,
  setFieldValue
}: {
  sizeLabel: string
  requireSize: boolean
  privateEntrance: boolean
  setFieldValue: any
}) => {
  const { t } = useTranslation()

  return (
    <Stack space="gutter" align="left">
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="count_bedrooms"
            label={t('Bedrooms', 'Bedrooms')}
            type="number"
            inputMode="numeric"
            required
          />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="count_dens"
            label={t('Den/flex', 'Den/flex')}
            type="number"
            inputMode="numeric"
          />
        </Column>
      </Columns>
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="count_full_bathrooms"
            label={t('Private bathrooms', 'Private bathrooms')}
            type="number"
            inputMode="decimal"
            required
          />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="count_full_shared_bathrooms"
            label={t('Shared bathrooms', 'Shared bathrooms')}
            type="number"
            inputMode="decimal"
            required
          />
        </Column>
      </Columns>
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="size"
            label={`${t('Shared listing size', 'Shared listing size')} ${sizeLabel}`}
            type="number"
            inputMode="numeric"
            required={requireSize}
          />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="max_occupancy"
            label={t('l.listing.edit.maximum_occupancy.label', 'Maximum occupancy')}
            type="number"
            inputMode="numeric"
          />
        </Column>
      </Columns>
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <Checkbox
            id="private_entrance"
            name="private_entrance"
            checked={privateEntrance}
            label={t('l.listing.edit.private_entrance.label', 'Private entrance')}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              const isChecked = e?.target?.checked
              setFieldValue('private_entrance', isChecked)
            }}
          />
        </Column>
      </Columns>
    </Stack>
  )
}

const RoomFields = ({ sizeLabel, requireSize }: { sizeLabel: string; requireSize: boolean }) => {
  const { t } = useTranslation()

  return (
    <Stack space="gutter" align="left">
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="size"
            label={`${t('l.listing.edit.bedroom_size.label', 'Bedroom size')} ${sizeLabel}`}
            type="number"
            inputMode="numeric"
            required={requireSize}
          />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="count_full_bathrooms"
            label={t('Private bathrooms', 'Private bathrooms')}
            type="number"
            inputMode="decimal"
            required
          />
        </Column>
      </Columns>
      <Columns space="gutter" collapseBelow="tablet">
        <Column width="1/2">
          <FieldInput
            name="count_full_shared_bathrooms"
            label={t('Shared bathrooms', 'Shared bathrooms')}
            type="number"
            inputMode="decimal"
            required
          />
        </Column>
        <Column width="1/2">
          <FieldInput
            name="max_occupancy"
            label={t('Maximum occupancy', 'Maximum occupancy')}
            type="number"
            inputMode="numeric"
          />
        </Column>
      </Columns>
    </Stack>
  )
}

const InfoSection = ({
  showUnitFields,
  isLoading
}: {
  showUnitFields?: boolean
  isLoading: boolean
}) => {
  const { t } = useTranslation()
  const { values, setFieldValue }: FormikContextType<DetailsFormValues> =
    useFormikContext<DetailsFormValues>()
  const profile = useProfile()
  const areaMeasurementUnit = profile?.setting_size_unit
  const sizeLabel = `(${getAreaMeasurementLabel(areaMeasurementUnit)})`
  const unitScope = values?.unit_type_scope_txt_id
  const isMultiUnitListing = values?.isGroupListing
  const state = values?.building?.state
  const requireSize = state !== 'ON'

  return (
    <FormBox label={t('Listing Information', 'Listing Information')}>
      {isLoading ? (
        <FormBoxSkeleton />
      ) : (
        <>
          {showUnitFields && (
            <UnitFields
              isMultiUnit={isMultiUnitListing}
              setFieldValue={setFieldValue}
              values={values}
            />
          )}
          {isMultiUnitListing ? (
            <MultiUnitListingFields sizeLabel={sizeLabel} />
          ) : (
            <FormSection label={t('Overview', 'Overview')}>
              {unitScope === 'entire' && (
                <EntireFields sizeLabel={sizeLabel} requireSize={requireSize} />
              )}
              {unitScope === 'partial' && (
                <PartialFields
                  sizeLabel={sizeLabel}
                  requireSize={requireSize}
                  privateEntrance={values.private_entrance}
                  setFieldValue={setFieldValue}
                />
              )}
              {unitScope === 'single' && (
                <RoomFields sizeLabel={sizeLabel} requireSize={requireSize} />
              )}
            </FormSection>
          )}
          <FormSection id="furnished" label={t('Furnishing options', 'Furnishing options')}>
            <FieldRadioBar
              label=""
              required
              aria-labelledby="furnished"
              name="furnished"
              onChange={(value: string) => {
                setFieldValue('furnished', value)
              }}
            >
              <RadioBarItem value="">
                {t('Furnished/Unfurnished', 'Furnished/Unfurnished')}
              </RadioBarItem>
              <RadioBarItem value="0">{t('common.unfurnished', 'Unfurnished')}</RadioBarItem>
              <RadioBarItem value="1">{t('common.furnished', 'Furnished')}</RadioBarItem>
            </FieldRadioBar>
          </FormSection>
          <DescriptionField />
          <PropertyRulesFields />
        </>
      )}
    </FormBox>
  )
}

export default InfoSection
